import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import Button from "@mui/material/Button";
import { ArrowRight } from "lucide-react";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ScienceIcon from "@mui/icons-material/Science";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import FactCheckIcon from "@mui/icons-material/FactCheck";

import "../assets/styles/Features.scss";
import { featuresData } from "../data/featuresData";
import shotSmartAlerts from "../assets/images/shots/smart_alerts.webp";
import shotSmartAlerts2x from "../assets/images/shots/smart_alerts@2x.webp";
import shotPreMatch from "../assets/images/shots/prematch.webp";
import shotPreMatch2x from "../assets/images/shots/prematch@2x.webp";
import shotBacktest from "../assets/images/shots/backtest.webp";
import shotBacktest2x from "../assets/images/shots/backtest@2x.webp";
import shotGuruVision from "../assets/images/shots/guru_vision.webp";
import shotGuruVision2x from "../assets/images/shots/guru_vision@2x.webp";
import shotLivePredictions from "../assets/images/shots/live_predictions.webp";
import shotLivePredictions2x from "../assets/images/shots/live_predictions@2x.webp";
import shotTrackRecord from "../assets/images/shots/track_record.webp";
import shotTrackRecord2x from "../assets/images/shots/track_record@2x.webp";

// App stills for the highlight cards (bezel-framed, 540×960 with a 2× copy).
// `focus` is the CSS object-position: which part of the phone the card crop shows.
const HIGHLIGHT_SHOTS: Record<string, { src: string; src2x: string; alt: string; focus?: string }> = {
  "smart-alerts": {
    src: shotSmartAlerts,
    src2x: shotSmartAlerts2x,
    alt: "Smart alert editor: second half, minutes 66 to 85, score level",
  },
  "pre-match-alerts": {
    src: shotPreMatch,
    src2x: shotPreMatch2x,
    alt: "Pre-match alert notification 30 minutes before kick-off",
    focus: "50% 1%",
  },
  backtesting: {
    src: shotBacktest,
    src2x: shotBacktest2x,
    alt: "Backtest report: triggers, trigger rate and aim hit rate over 14 days",
  },
  "guru-vision": {
    src: shotGuruVision,
    src2x: shotGuruVision2x,
    alt: "Guru Vision pre-match predictions and strongest calls",
  },
  "live-predictions": {
    src: shotLivePredictions,
    src2x: shotLivePredictions2x,
    alt: "Live in-play prediction at 64': match result probabilities and the strongest live calls",
    focus: "50% 22%",
  },
  "track-record": {
    src: shotTrackRecord,
    src2x: shotTrackRecord2x,
    alt: "Track record: share of picks that came true over 30 days, sure picks and strongest picks",
    focus: "50% 20%",
  },
};

const HIGHLIGHT_ICONS: Record<string, React.ReactNode> = {
  "smart-alerts": <NotificationsActiveIcon className="feature-icon" />,
  "pre-match-alerts": <ScheduleIcon className="feature-icon" />,
  backtesting: <ScienceIcon className="feature-icon" />,
  "guru-vision": <AutoAwesomeIcon className="feature-icon" />,
  "live-predictions": <TrendingUpIcon className="feature-icon" />,
  "track-record": <FactCheckIcon className="feature-icon" />,
};

function Features() {
  useEffect(() => {
    AOS.init({ duration: 900, once: true, easing: "ease-out-cubic" });

    const timeout1 = setTimeout(() => AOS.refresh(), 500);
    const timeout2 = setTimeout(() => AOS.refresh(), 1500);
    const timeout3 = setTimeout(() => AOS.refresh(), 3000);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, []);

  const highlightedFeatures = featuresData.filter((feature) => feature.highlight);

  return (
    <div className="features-outer" id="features">
      <div className="features-header" data-aos="fade-up">
        <span className="section-eyebrow">Features</span>
        <h2 className="section-title">Everything a Football Analytics Platform Should Do</h2>
        <p className="section-description">
          From real-time football stats to AI-powered match analysis — Goal
          Guru puts the full picture in your pocket.
        </p>
      </div>

      <div className="features-grid">
        {highlightedFeatures.map((feature, index) => (
          <div
            className="feature-highlight-card"
            key={feature.id}
            data-aos="fade-up"
            data-aos-delay={100 + index * 80}
          >
            {HIGHLIGHT_SHOTS[feature.id] ? (
              <div className="feature-highlight-media">
                <img
                  src={HIGHLIGHT_SHOTS[feature.id].src}
                  srcSet={`${HIGHLIGHT_SHOTS[feature.id].src} 1x, ${HIGHLIGHT_SHOTS[feature.id].src2x} 2x`}
                  alt={HIGHLIGHT_SHOTS[feature.id].alt}
                  width={540}
                  height={960}
                  loading="lazy"
                  style={HIGHLIGHT_SHOTS[feature.id].focus ? { objectPosition: HIGHLIGHT_SHOTS[feature.id].focus } : undefined}
                />
              </div>
            ) : (
              <div className="feature-highlight-media feature-highlight-media--plain" aria-hidden="true" />
            )}
            <div className="feature-icon-wrap">
              {HIGHLIGHT_ICONS[feature.id]}
            </div>
            <h3>{feature.title}</h3>
            <p>{feature.shortDescription ?? feature.description}</p>
            <RouterLink to={feature.link} className="feature-learn-more">
              Learn more
              <span className="visually-hidden"> about {feature.title}</span>
              <ArrowRight className="feature-learn-more-arrow" />
            </RouterLink>
          </div>
        ))}
      </div>

      <div className="features-view-all">
        <Button
          variant="contained"
          className="read-more-btn"
          component={RouterLink}
          to="/features/"
        >
          View All Features
        </Button>
      </div>
    </div>
  );
}

export default Features;
